import moment from 'moment';
import { Gender, GenderId } from '../../../enums/EnumsValues';

/**
 * Configuración de búsqueda de nombres en frente del DNI
 */
const NAME_SEARCH_CONFIG = {
  /**
   * Número máximo de líneas a buscar después de "Nombre"/"Apellido"
   * Aumentado a 5 para manejar casos donde el OCR inserta múltiples líneas
   * intermedias (encabezados, números de documento, etc.)
   */
  MAX_LINES_TO_CHECK: 5,
};

/**
 * Hook para extracción de datos de DNI argentino
 * Soporta múltiples versiones de DNI mediante estrategias de parsing adaptativas
 */
export const useDniBackExtraction = () => {
  // ============================================================================
  // UTILIDADES DE NORMALIZACIÓN
  // ============================================================================

  /**
   * Normaliza texto para comparaciones flexibles
   */
  const normalizeText = (text: string): string => {
    if (!text) return '';
    return text
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover acentos
      .replace(/\s+/g, ' ') // Normalizar espacios
      .trim();
  };

  /**
   * Calcula distancia de Levenshtein entre dos strings
   * Retorna el número de operaciones (inserción, eliminación, sustitución) necesarias
   * para transformar str1 en str2
   */
  const levenshteinDistance = (str1: string, str2: string): number => {
    const len1 = str1.length;
    const len2 = str2.length;
    const matrix: number[][] = Array(len1 + 1)
      .fill(null)
      .map(() => Array(len2 + 1).fill(0));

    for (let i = 0; i <= len1; i++) matrix[i][0] = i;
    for (let j = 0; j <= len2; j++) matrix[0][j] = j;

    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }

    return matrix[len1][len2];
  };

  /**
   * Busca una línea por palabra clave de forma flexible
   * Soporta fuzzy matching para tolerar errores de OCR
   * @param data - Array de líneas
   * @param keywords - Palabras clave a buscar
   * @param exact - Si es true, solo búsqueda exacta
   * @param useFuzzy - Si es true, usa fuzzy matching como fallback
   * @param threshold - Máximo número de diferencias permitidas en fuzzy matching
   */
  const findLineByKeyword = (
    data: string[],
    keywords: string[],
    exact: boolean = false,
    useFuzzy: boolean = false,
    threshold: number = 2
  ): number => {
    // Primero intentar búsqueda exacta (rápida)
    for (let i = 0; i < data.length; i++) {
      const normalizedLine = normalizeText(data[i]);
      for (const keyword of keywords) {
        const normalizedKeyword = normalizeText(keyword);
        if (exact) {
          if (normalizedLine === normalizedKeyword) return i;
        } else {
          if (normalizedLine.includes(normalizedKeyword)) return i;
        }
      }
    }

    // Si no se encuentra y fuzzy matching está habilitado
    if (useFuzzy) {
      for (let i = 0; i < data.length; i++) {
        const normalized = normalizeText(data[i]);
        const words = normalized.split(/\s+/);
        
        for (const keyword of keywords) {
          const normalizedKeyword = normalizeText(keyword);
          const keywordWords = normalizedKeyword.split(/\s+/);
          
          // Verificar si alguna palabra es similar
          for (const word of words) {
            for (const kwWord of keywordWords) {
              // Solo comparar palabras de longitud significativa
              if (word.length > 3 && kwWord.length > 3) {
                const distance = levenshteinDistance(word, kwWord);
                if (distance <= threshold) {
                  if (process.env.NODE_ENV === 'development') {
                    console.log(`[fuzzy match] "${word}" ≈ "${kwWord}" (distancia: ${distance})`);
                  }
                  return i;
                }
              }
            }
          }
        }
      }
    }

    return -1;
  };

  /**
   * Formatea línea MRZ (Machine Readable Zone)
   */
  const formatLine = (line: string): string => {
    if (!line) return '';
    const lineWithoutSpaces = line?.replace(/\s/g, '');
    const lineWithEqualSpecialCharacter = lineWithoutSpaces?.replace(
      /く/g,
      '<',
    );
    return lineWithEqualSpecialCharacter;
  };

  /**
   * Limpia prefijos comunes de lugar de nacimiento
   */
  const cleanPlaceOfBirthPrefixes = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/^PROV\.\s*DE\s*/i, '')
      .replace(/^PROVINCIA\s*DE\s*/i, '')
      .replace(/^LUGAR\s*DE\s*NACIMIENTO/i, '')
      .trim();
  };

  // ============================================================================
  // EXTRACCIÓN DE DNI
  // ============================================================================

  const dni = (data: string[]): string => {
    const idargIndex = data.findIndex((item) => item.includes('IDARG'));
    if (idargIndex === -1) return '';
    const idarg = data[idargIndex];
    const formatedLine = formatLine(idarg);
    const dni = formatedLine?.split('IDARG')[1]?.split('<')[0];
    return dni?.trim();
  };

  // ============================================================================
  // EXTRACCIÓN DE LUGAR DE NACIMIENTO - MÚLTIPLES ESTRATEGIAS
  // ============================================================================

  /**
   * Valida que el valor parezca un nombre de lugar válido
   * Rechaza códigos alfanuméricos, IDs, hashes, etc.
   */
  const isValidPlaceOfBirth = (value: string): boolean => {
    if (!value || value.trim().length < 3) return false;
    
    const normalized = normalizeText(value);
    
    // Rechazar si es solo números
    if (/^\d+$/.test(value.trim())) return false;
    
    // Rechazar si tiene muchos números mezclados con letras (códigos alfanuméricos)
    // Ej: "4E6E60AD142682281022137", "9/a/2"
    const digitCount = (value.match(/\d/g) || []).length;
    
    // Si tiene más del 40% de dígitos, probablemente es un código
    if (digitCount > 0 && digitCount / value.length > 0.4) return false;
    
    // Rechazar si tiene patrones de código (muchas letras y números mezclados sin espacios)
    // Ej: "4E6E60AD142682281022137"
    if (/^[A-Z0-9]{10,}$/i.test(value.trim())) return false;
    
    // Rechazar si tiene barras con números (ej: "9/a/2")
    if (/\d+\/[a-z0-9]+\/\d+/i.test(value)) return false;
    
    // Rechazar palabras clave de otras secciones
    if (
      normalized.includes('DOMICILIO') ||
      normalized.includes('CUIL') ||
      normalized.includes('TRAMITE') ||
      normalized.includes('HUELLA') ||
      normalized.includes('DACTILAR') ||
      normalized.includes('IDARG')
    ) {
      return false;
    }
    
    return true;
  };

  /**
   * Estrategia 1: DNI Nuevo - Formato con salto de línea o en misma línea
   * Ejemplo 1: "Lugar de nacimiento\nPROV. DE ENTRE RIOS"
   * Ejemplo 2: "LUGAR DE NACIMIENTO PROV. DE ENTRE RIOS"
   * Soporta fuzzy matching para errores de OCR (ej: "NAGMENTO" por "NACIMIENTO")
   */
  const placeOfBirthStrategy1 = (data: string[]): string => {
    const keywords = ['Lugar de nacimiento', 'LUGAR DE NACIMIENTO'];
    // Intentar búsqueda exacta primero
    let index = findLineByKeyword(data, keywords, false, false);
    
    // Si falla, intentar fuzzy matching
    if (index === -1) {
      index = findLineByKeyword(data, ['LUGAR DE NACIMIENTO'], false, true, 2);
    }
    
    if (index !== -1) {
      const line = data[index];
      const normalized = normalizeText(line);
      
      // Validar que no sea "Fecha de nacimiento"
      if (normalized.includes('FECHA') || normalized.includes('DATE')) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[placeOfBirth] Estrategia 1 - Rechazado: es fecha de nacimiento, no lugar');
        }
        return '';
      }
      
      // Caso 1: Valor en la misma línea
      // "LUGAR DE NACIMIENTO PROV. DE ENTRE RIOS" o "LUGAR DE NACIMIENTO: CIUDAD DE BUENOS AIRES"
      // "COUGAR DE NACIMIENTO PROV. DE ENTRE RIOS" (error común de OCR)
      // Regex flexible que acepta variaciones de LUGAR y NACIMIENTO (errores de OCR) y dos puntos opcionales
      const regex = /(?:LUGAR|COUGAR|CUGAR)\s*DE\s*[A-Z]+:?\s+(.+)/i;
      const match = line.match(regex);
      
      if (match && match[1]) {
        const value = match[1].trim();
        // Validar que sea un lugar válido (rechaza códigos alfanuméricos)
        if (isValidPlaceOfBirth(value)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[placeOfBirth] Estrategia 1 - Caso: misma línea (regex flexible con fuzzy)');
          }
          return cleanPlaceOfBirthPrefixes(value);
        } else if (process.env.NODE_ENV === 'development') {
          console.log('[placeOfBirth] Estrategia 1 - Rechazado (misma línea): valor inválido:', value);
        }
      }
      
      // Caso 2: Valor en línea siguiente
      // "Lugar de nacimiento\nPROV. DE ENTRE RIOS"
      if (index + 1 < data.length) {
        const nextLine = data[index + 1];
        
        // Validar que sea un lugar válido (rechaza códigos alfanuméricos)
        if (isValidPlaceOfBirth(nextLine)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[placeOfBirth] Estrategia 1 - Caso: línea siguiente');
          }
          return cleanPlaceOfBirthPrefixes(nextLine);
        } else if (process.env.NODE_ENV === 'development') {
          console.log('[placeOfBirth] Estrategia 1 - Rechazado (línea siguiente): valor inválido:', nextLine);
        }
      }
    }
    return '';
  };

  /**
   * Estrategia 2: DNI Antiguo - Todo en una línea
   * Ejemplo: "LUGAR DENACIMIENTO ENTRE RIOS"
   */
  const placeOfBirthStrategy2 = (data: string[]): string => {
    const placeOfBirthFound = data?.find((item) =>
      normalizeText(item).includes('LUGAR') && 
      normalizeText(item).includes('NACIMIENTO')
    );

    if (!placeOfBirthFound) return '';

    const normalized = normalizeText(placeOfBirthFound);
    
    // Ignorar si es "FECHA Y LUGAR"
    if (normalized.includes('FECHA Y LUGAR')) {
      return '';
    }

    // Extraer texto después de "LUGAR DE NACIMIENTO" o "LUGAR DENACIMIENTO"
    const regex = /LUGAR\s*D?E?\s*NACIMIENTO\s+(.+)/i;
    const match = placeOfBirthFound.match(regex);
    
    if (match && match[1]) {
      const value = match[1].trim();
      // Validar que no sea solo números y tenga longitud mínima
      if (value.length > 2 && !/^\d+$/.test(value)) {
        return cleanPlaceOfBirthPrefixes(value);
      }
    }

    return '';
  };

  /**
   * Estrategia 3: DNI Extranjeros
   * Ejemplo: Busca "INGRESO" y toma línea anterior
   */
  const placeOfBirthStrategy3 = (data: string[]): string => {
    const dateOfEntryIntoCountryIndex = data?.findIndex((item) =>
      normalizeText(item).includes('INGRESO'),
    );

    if (dateOfEntryIntoCountryIndex !== -1 && dateOfEntryIntoCountryIndex > 0) {
      const line1 = data[dateOfEntryIntoCountryIndex - 1];
      return line1?.replace(/LUGAR\s*DE\s*NACIMIENTO/i, '')?.trim();
    }
    return '';
  };

  /**
   * Estrategia 4: Formato "FECHA Y LUGAR"
   */
  const placeOfBirthStrategy4 = (data: string[]): string => {
    const indexPlaceOfBirth = findLineByKeyword(data, ['FECHA Y LUGAR']);
    if (indexPlaceOfBirth !== -1 && indexPlaceOfBirth + 1 < data.length) {
      return data[indexPlaceOfBirth + 1];
    }
    return '';
  };

  /**
   * Función principal de lugar de nacimiento con múltiples estrategias
   */
  const placeOfBirth = (data: string[]): string => {
    // Intentar estrategia 1: DNI Nuevo
    const result1 = placeOfBirthStrategy1(data);
    if (result1) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[placeOfBirth] Estrategia 1 (DNI Nuevo):', result1);
      }
      return result1;
    }

    // Intentar estrategia 3: Extranjeros (antes que estrategia 2 por prioridad)
    const result3 = placeOfBirthStrategy3(data);
    if (result3) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[placeOfBirth] Estrategia 3 (Extranjeros):', result3);
      }
      return result3;
    }

    // Intentar estrategia 4: Fecha y Lugar
    const result4 = placeOfBirthStrategy4(data);
    if (result4) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[placeOfBirth] Estrategia 4 (Fecha y Lugar):', result4);
      }
      return result4;
    }

    // Intentar estrategia 2: DNI Antiguo (última porque es más genérica)
    const result2 = placeOfBirthStrategy2(data);
    if (result2) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[placeOfBirth] Estrategia 2 (DNI Antiguo):', result2);
      }
      return result2;
    }

    return '';
  };

  // ============================================================================
  // EXTRACCIÓN DE DOMICILIO - MÚLTIPLES ESTRATEGIAS
  // ============================================================================

  /**
   * Estrategia 1: DNI Nuevo - Con dos puntos o multilínea
   * Ejemplo 1: "Domicilio: LA RIOJA 362 4 17 PARANÁ PARANÁ - ENTRE RIOS"
   * Ejemplo 2: "Domicilio\nZAMUDIO 1268\nEZPELETA QUILMES BUENOS AIRES"
   */
  const homeStrategy1 = (data: string[]): string => {
    // Intentar búsqueda exacta primero
    let domicilioIndex = findLineByKeyword(data, ['Domicilio', 'DOMICILIO'], false, false);
    
    // Si falla, intentar fuzzy matching
    if (domicilioIndex === -1) {
      domicilioIndex = findLineByKeyword(data, ['DOMICILIO'], false, true, 2);
    }
    
    if (domicilioIndex !== -1) {
      const line = data[domicilioIndex];
      const regex = /Domicilio:?\s*(.+)/i;
      const match = line.match(regex);

      let address = '';

      // Caso 1: Domicilio con valor en misma línea
      if (match && match[1] && match[1].trim().length > 3) {
        address = match[1].trim();
      }

      // Caso 2: Domicilio en línea(s) siguiente(s)
      // Concatenar múltiples líneas hasta encontrar otra palabra clave
      if (domicilioIndex + 1 < data.length) {
        let currentIndex = domicilioIndex + 1;
        const addressLines: string[] = [];
        
        // Si ya hay contenido en la misma línea, empezar desde ahí
        if (address) {
          addressLines.push(address);
        }
        
        // Continuar leyendo líneas hasta encontrar palabra clave
        while (currentIndex < data.length) {
          const nextLine = data[currentIndex];
          const normalized = normalizeText(nextLine);
          
          // Detener si encontramos otra sección
          // Incluir variantes de OCR de "LUGAR" (COUGAR, CUGAR)
          if (
            normalized.includes('LUGAR') ||
            normalized.includes('COUGAR') ||  // Variante común de OCR
            normalized.includes('CUGAR') ||   // Otra variante de OCR
            normalized.includes('CUIL') ||
            normalized.includes('TRAMITE') ||
            normalized.includes('HIJO') ||
            normalized.includes('IDARG') ||
            nextLine.trim().length < 3
          ) {
            break;
          }
          
          addressLines.push(nextLine.trim());
          currentIndex++;
          
          // Límite de seguridad: máximo 3 líneas
          if (addressLines.length >= 3) break;
        }
        
        if (addressLines.length > 0) {
          address = addressLines.join(' ');
        }
      }

      if (address && address.length > 3) {
        return address;
      }
    }
    return '';
  };

  /**
   * Estrategia 2: DNI Antiguo - Todo junto hasta LUGAR
   * Ejemplo: "DOMICILIO CHAÑAR 253 UBAJAY COLÓN ENTRE RIOS"
   */
  const homeStrategy2 = (data: string[]): string => {
    let homeResponse = '';
    const birthPlaceIndex = findLineByKeyword(data, ['LUGAR']);

    if (birthPlaceIndex === -1) return '';

    for (let index = 0; index < birthPlaceIndex; index++) {
      const element = data[index];
      homeResponse += element + ' ';
    }
    
    const cleaned = homeResponse
      ?.replace(/DOMICILIO:?/i, '')
      ?.trim();
    
    if (cleaned && cleaned.length > 3) {
      return cleaned;
    }
    return '';
  };

  /**
   * Estrategia 3: Búsqueda explícita de DOMICILIO
   */
  const homeStrategy3 = (data: string[]): string => {
    const domicilioIndex = findLineByKeyword(data, ['DOMICILIO']);
    
    if (domicilioIndex !== -1) {
      let homeResponse = data[domicilioIndex];
      
      // Buscar hasta encontrar LUGAR o CUIL
      for (let i = domicilioIndex + 1; i < data.length; i++) {
        const normalized = normalizeText(data[i]);
        if (normalized.includes('LUGAR') || normalized.includes('CUIL')) {
          break;
        }
        homeResponse += ' ' + data[i];
      }
      
      const cleaned = homeResponse
        ?.replace(/DOMICILIO:?/i, '')
        ?.trim();
      
      if (cleaned && cleaned.length > 3) {
        return cleaned;
      }
    }
    return '';
  };

  /**
   * Función principal de domicilio con múltiples estrategias
   */
  const home = (data: string[]): string => {
    // Intentar estrategia 1: DNI Nuevo con dos puntos
    const result1 = homeStrategy1(data);
    if (result1) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[home] Estrategia 1 (DNI Nuevo):', result1);
      }
      return result1;
    }

    // Intentar estrategia 3: Búsqueda explícita
    const result3 = homeStrategy3(data);
    if (result3) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[home] Estrategia 3 (Búsqueda explícita):', result3);
      }
      return result3;
    }

    // Intentar estrategia 2: DNI Antiguo
    const result2 = homeStrategy2(data);
    if (result2) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[home] Estrategia 2 (DNI Antiguo):', result2);
      }
      return result2;
    }

    return '';
  };

  // ============================================================================
  // EXTRACCIÓN DE FECHA DE NACIMIENTO
  // ============================================================================

  const birthDate = (data: string[]) => {
    let yearToUse = '';
    const idargIndex = data.findIndex((item) => item.includes('IDARG'));
    if (idargIndex === -1) return '';
    const dates = data[idargIndex + 1];
    const formatedLine = formatLine(dates);
    const nowYear = moment().year().toString()?.slice(2, 4);
    const year = formatedLine?.slice(0, 2);
    if (year > nowYear) {
      yearToUse = '19' + year;
    } else {
      yearToUse = '20' + year;
    }
    const month = formatedLine?.slice(2, 4);
    const day = formatedLine?.slice(4, 6);
    const birthDate = yearToUse + month + day;
    return moment(birthDate, 'YYYYMMDD');
  };

  // ============================================================================
  // EXTRACCIÓN DE GÉNERO
  // ============================================================================

  const gender = (data: string[]) => {
    const idargIndex = data.findIndex((item) => item.includes('IDARG'));
    if (idargIndex === -1) return undefined;
    const dates = data[idargIndex + 1];
    const formatedLine = formatLine(dates);
    const gender = formatedLine?.slice(7, 8)?.trim();
    switch (gender?.toUpperCase()) {
      case Gender.Masculino:
        return GenderId.Masculino;
      case Gender.Femenino:
        return GenderId.Femenino;
      case Gender.Otro:
        return GenderId.Otro;
      default:
        return undefined;
    }
  };

  // ============================================================================
  // EXTRACCIÓN DE CUIL
  // ============================================================================

  /**
   * Extrae CUIL con estrategias múltiples
   * Estrategia 1: CUIL en misma línea - "CUIL: 20-41043008-9"
   * Estrategia 2: CUIL en línea separada - "CUIL\n27-39841265-1"
   * Usa regex para extraer solo el CUIL (formato XX-XXXXXXXX-X), ignorando números adicionales
   */
  const cuil = (data: string[]): string => {
    // Buscar línea que contenga "CUIL"
    let cuilIndex = data.findIndex((item) => 
      normalizeText(item).includes('CUIL')
    );
    
    // Si falla, intentar fuzzy matching
    if (cuilIndex === -1) {
      cuilIndex = findLineByKeyword(data, ['CUIL'], false, true, 1);
    }
    
    if (cuilIndex === -1) return '';
    
    const line = data[cuilIndex];
    
    // Estrategia 1: CUIL en la misma línea
    // "CUIL: 20-41043008-9" o "CUIL 20-41043008-9"
    const regex = /CUIL:?\s*(.+)/i;
    const match = line.match(regex);
    
    if (match && match[1] && match[1].trim().length > 5) {
      // Hay contenido después de "CUIL"
      // Extraer solo el CUIL con formato XX-XXXXXXXX-X
      const cuilMatch = match[1].match(/(\d{2}[-\s]?\d{8}[-\s]?\d)/);
      if (cuilMatch) {
        const cuil = cuilMatch[1].replace(/[^a-zA-Z0-9 ]/g, '');
        if (process.env.NODE_ENV === 'development') {
          console.log('[cuil] Estrategia 1 (misma línea - regex):', cuil?.trim());
        }
        return cuil?.trim();
      }
      
      // Fallback: limpiar toda la línea
      const cuil = match[1].replace(/[^a-zA-Z0-9 ]/g, '');
      if (process.env.NODE_ENV === 'development') {
        console.log('[cuil] Estrategia 1 (misma línea - completa):', cuil?.trim());
      }
      return cuil?.trim();
    }
    
    // Estrategia 2: CUIL en líneas siguientes (buscar en las próximas 3 líneas)
    // "CUIL\n27-39841265-1" o "CUIL:\nTrámite N°\n27-99999999-0 00990060090"
    for (let offset = 1; offset <= 3 && cuilIndex + offset < data.length; offset++) {
      const nextLine = data[cuilIndex + offset];
      const normalized = normalizeText(nextLine);
      
      // Validar que no sea palabra clave de otra sección
      // NO rechazar "TRAMITE" porque puede estar entre CUIL y el valor
      if (
        nextLine &&
        nextLine.trim().length > 5 &&
        !normalized.includes('LUGAR') &&
        !normalized.includes('DOMICILIO') &&
        !normalized.includes('IDARG') &&
        !normalized.includes('HIJO')
      ) {
        // Verificar que contenga números
        if (/\d/.test(nextLine)) {
          // Extraer solo el CUIL (formato XX-XXXXXXXX-X)
          // Ignora números adicionales como número de trámite
          const cuilMatch = nextLine.match(/(\d{2}[-\s]?\d{8}[-\s]?\d)/);
          
          if (cuilMatch) {
            const cuil = cuilMatch[1].replace(/[^a-zA-Z0-9 ]/g, '');
            if (process.env.NODE_ENV === 'development') {
              console.log(`[cuil] Estrategia 2 (línea +${offset} - regex):`, cuil?.trim());
            }
            return cuil?.trim();
          }
        }
      }
    }
    
    return '';
  };

  // ============================================================================
  // EXTRACCIÓN DE NACIONALIDAD
  // ============================================================================

  /**
   * Estrategia 1: Extraer nacionalidad desde línea MRZ (dorso)
   * Formato MRZ línea 2: YYMMDDXYYMMDDCCC<<<<<<<<<<<<C
   * Posiciones 14-16: Código de nacionalidad (3 letras)
   */
  const nationalityStrategy1 = (data: string[]): string | undefined => {
    const idargIndex = data.findIndex((item) => item.includes('IDARG'));
    if (idargIndex === -1 || idargIndex + 1 >= data.length) return undefined;
    const dates = data[idargIndex + 1];
    const formatedLine = formatLine(dates);
    const nationality = formatedLine?.trim()?.replace(' ', '')?.slice(14, 17);
    
    // Validar que sea código válido (3 letras, sin caracteres de relleno)
    if (nationality && nationality.length === 3 && !/[<>]/.test(nationality)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[nationality] Estrategia 1 (MRZ):', nationality);
      }
      return nationality;
    }
    return undefined;
  };

  /**
   * Estrategia 2: Extraer nacionalidad desde frente del DNI
   * Busca "Nacionalidad" y toma valor después de línea de sexo
   */
  const nationalityStrategy2 = (data: string[]): string | undefined => {
    // Buscar línea con "Nacionalidad"
    let index = findLineByKeyword(data, ['Nacionalidad', 'NACIONALIDAD', 'Nationality'], false, false);
    
    if (index === -1) {
      // Intentar fuzzy
      index = findLineByKeyword(data, ['NACIONALIDAD'], false, true, 2);
    }
    
    if (index === -1) return undefined;
    
    // Buscar el valor después de esta línea
    // Puede estar en las siguientes 2-4 líneas
    for (let i = index + 1; i < Math.min(index + 5, data.length); i++) {
      const line = data[i].trim();
      const normalized = normalizeText(line);
      
      // Buscar línea que parezca una nacionalidad
      if (
        line.length > 3 &&
        !normalized.includes('SEX') &&
        !normalized.includes('SEXO') &&
        !normalized.includes('DOCUMENTO') &&
        !normalized.includes('FECHA') &&
        !/^[MFX]$/.test(line) // No es solo M, F o X
      ) {
        // Verificar si parece una nacionalidad (mayúsculas, letras)
        if (/^[A-Z\s]+$/.test(line)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[nationality] Estrategia 2 (frente):', line);
          }
          return line;
        }
      }
    }
    
    return undefined;
  };

  /**
   * Función principal de nacionalidad con múltiples estrategias
   * Prioriza frente sobre MRZ porque el frente tiene el nombre completo del país
   */
  const nationality = (data: string[]): string | undefined => {
    // Estrategia 2: Desde frente del DNI (PRIMERO)
    const nationalityFromFront = nationalityStrategy2(data);
    if (nationalityFromFront) {
      // Convertir nombre completo a código ISO si es posible
      const normalized = normalizeText(nationalityFromFront);
      if (normalized.includes('ARGENTINA')) return 'ARG';
      if (normalized.includes('BRASIL')) return 'BRA';
      if (normalized.includes('CHILE')) return 'CHL';
      if (normalized.includes('URUGUAY')) return 'URY';
      if (normalized.includes('PARAGUAY')) return 'PRY';
      if (normalized.includes('BOLIVIA')) return 'BOL';
      if (normalized.includes('PERU')) return 'PER';
      if (normalized.includes('VENEZUELA')) return 'VEN';
      if (normalized.includes('COLOMBIA')) return 'COL';
      if (normalized.includes('ECUADOR')) return 'ECU';
      
      // Si no se puede convertir, retornar tal cual
      return nationalityFromFront;
    }
    
    // Estrategia 1: Desde línea MRZ (dorso) - FALLBACK
    const result1 = nationalityStrategy1(data);
    if (result1) return result1;
    
    return undefined;
  };

  // ============================================================================
  // EXTRACCIÓN DE NOMBRES - MÚLTIPLES ESTRATEGIAS
  // ============================================================================

  /**
   * Estrategia 1: Línea MRZ (Machine Readable Zone)
   * Ejemplo: "DROZ<<PATRICIA<NOEMI<<<<<<<<<<" o "TORRES<<ROCIO<NICOLE<<<<<<<<<<<"
   */
  const getNamesStrategy1 = ({
    data,
    type,
  }: {
    data: string[];
    type: 'firstname' | 'lastname';
  }): string => {
    let lastLane = data[data.length - 1];

    if (!lastLane.includes('<')) {
      lastLane = data[data.length - 2];
    }

    if (!lastLane.includes('<')) {
      return '';
    }

    const lastLaneWithEqualSpecialCharacter = formatLine(lastLane);

    const nameString =
      lastLaneWithEqualSpecialCharacter?.split('<<')[
        type === 'firstname' ? 1 : 0
      ];
    const nameStringWithoutSimbol = nameString?.replace(/<+/g, ' ');
    const nameStringWithoutSimbolAndTrimed = nameStringWithoutSimbol?.trim();
    return nameStringWithoutSimbolAndTrimed;
  };

  /**
   * Valida que una línea sea un nombre válido y no parte del encabezado del DNI
   * Rechaza líneas que contengan palabras del encabezado o patrones inválidos
   */
  const isValidName = (line: string): boolean => {
    if (!line || line.trim().length < 2) return false;
    
    const normalized = normalizeText(line);
    
    // Rechazar palabras del encabezado del DNI
    const headerKeywords = [
      'DOCUMENTO',
      'NACIONAL',
      'IDENTIDAD',
      'IDENTIDADE', // Variante con error de OCR
      'REPUBLICA',
      'MERCOSUR',
      'REGISTRO',
      'MINISTERIO',
      'INTERIOR',
      'TRANSPORTE',
      'PERSONAS'
    ];
    
    for (const keyword of headerKeywords) {
      if (normalized.includes(keyword)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[isValidName] Rechazado - contiene palabra de encabezado "${keyword}":`, line);
        }
        return false;
      }
    }
    
    // Rechazar líneas muy largas (probablemente encabezados)
    if (line.trim().length > 40) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[isValidName] Rechazado - línea muy larga (>40 chars):', line);
      }
      return false;
    }
    
    // Rechazar si es solo números (probablemente DNI o código)
    const trimmed = line.trim();
    if (/^\d+$/.test(trimmed)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[isValidName] Rechazado - solo números:', line);
      }
      return false;
    }
    
    // Rechazar formato de DNI argentino (XX.XXX.XXX o XXXXXXXX)
    if (/^\d{1,2}\.\d{3}\.\d{3}$/.test(trimmed) || /^\d{8,9}$/.test(trimmed)) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[isValidName] Rechazado - formato DNI:', line);
      }
      return false;
    }
    
    // Rechazar si tiene demasiados números (>50% de la línea son dígitos)
    const digitCount = (trimmed.match(/\d/g) || []).length;
    if (digitCount > 0 && digitCount / trimmed.length > 0.5) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[isValidName] Rechazado - demasiados números (>50%):', line);
      }
      return false;
    }
    
    // Rechazar otras palabras clave de secciones del DNI
    const sectionKeywords = [
      'APELLIDO',
      'SURNAME',
      'NOMBRE',
      'NAME',
      'SEXO',
      'SEX',
      'NACIONALIDAD',
      'NATIONALITY',
      'FECHA',
      'DATE',
      'EJEMPLAR',
      'TRAMITE'
    ];
    
    for (const keyword of sectionKeywords) {
      if (normalized === keyword || normalized.includes(`${keyword}/`) || normalized.includes(`/${keyword}`)) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[isValidName] Rechazado - es palabra clave de sección "${keyword}":`, line);
        }
        return false;
      }
    }
    
    return true;
  };

  /**
   * Estrategia 2: Búsqueda en texto del frente
   * Busca "Apellido" o "Nombre" y toma la línea siguiente
   * Usa word boundaries para evitar matches en substrings (ej: "Name" dentro de "Surname")
   * Soporta fuzzy matching para errores de OCR
   * Valida que la línea sea un nombre válido y busca en múltiples líneas si es necesario
   */
  const getNamesStrategy2 = ({
    data,
    type,
  }: {
    data: string[];
    type: 'firstname' | 'lastname';
  }): string => {
    // Usar solo palabras en español para evitar substring matches
    // "Surname" contiene "Name" como substring, causando problemas
    const keywords =
      type === 'lastname'
        ? ['Apellido', 'APELLIDO']
        : ['Nombre', 'NOMBRE'];

    // Buscar línea que contenga la palabra clave como palabra completa
    let index = -1;
    for (let i = 0; i < data.length; i++) {
      const line = data[i];
      for (const keyword of keywords) {
        // Buscar palabra completa usando word boundaries
        const regex = new RegExp(`\\b${keyword}\\b`, 'i');
        if (regex.test(line)) {
          index = i;
          break;
        }
      }
      if (index !== -1) break;
    }

    // Si no se encuentra, intentar fuzzy matching
    if (index === -1) {
      index = findLineByKeyword(data, keywords, false, true, 2);
    }

    if (index !== -1 && index + 1 < data.length) {
      // Buscar en las siguientes líneas hasta encontrar un nombre válido
      // Usa configuración para permitir ajustes fáciles según casos de OCR
      const maxLinesToCheck = NAME_SEARCH_CONFIG.MAX_LINES_TO_CHECK;
      
      for (let offset = 1; offset <= maxLinesToCheck && index + offset < data.length; offset++) {
        const nextIndex = index + offset;
        const nextLine = data[nextIndex];
        const normalized = normalizeText(nextLine);

        // En DNI extranjero la leyenda "EXTRANJERO" aparece justo debajo
        // de "Apellido"; no debe considerarse como apellido - saltar esta línea
        if (
          type === 'lastname' &&
          normalized === 'EXTRANJERO'
        ) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[${type}] Línea +${offset} rechazada: es "EXTRANJERO"`);
          }
          continue; // Continuar buscando en la siguiente línea
        }

        // Validar que la línea sea un nombre válido
        if (nextLine && nextLine.trim().length > 1 && isValidName(nextLine)) {
          if (process.env.NODE_ENV === 'development' && offset > 1) {
            console.log(`[${type}] Estrategia 2 - Usando línea +${offset} (líneas anteriores inválidas)`);
          }
          return nextLine.trim();
        } else if (process.env.NODE_ENV === 'development') {
          console.log(`[${type}] Línea +${offset} rechazada por isValidName:`, nextLine);
        }
      }
    }
    return '';
  };

  /**
   * Función principal de nombres con múltiples estrategias
   * Prioriza frente sobre MRZ porque:
   * - El frente preserva caracteres especiales (ñ, acentos) según normativa RENAPER
   * - La MRZ normaliza caracteres según estándares OACI (MUÑOZ → MUNOZ, RAÚL → RAUL)
   * - isValidName() rechaza errores de OCR del frente (números, encabezados)
   * - MRZ se usa como fallback confiable si frente no es legible
   */
  const getNames = ({
    data,
    type,
  }: {
    data: string[];
    type: 'firstname' | 'lastname';
  }): string => {
    // Intentar estrategia 2: Búsqueda en frente (PRIMERO - preserva ñ y acentos)
    // La validación isValidName() rechaza errores de OCR
    const result2 = getNamesStrategy2({ data, type });
    if (result2) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${type}] Estrategia 2 (Búsqueda frente - con acentos):`, result2);
      }
      return result2;
    }

    // Intentar estrategia 1: MRZ (FALLBACK - normaliza caracteres pero confiable)
    // Usar solo si frente no es legible o fue rechazado por validación
    const result1 = getNamesStrategy1({ data, type });
    if (result1) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[${type}] Estrategia 1 (MRZ - normalizado):`, result1);
      }
      return result1;
    }

    return '';
  };

  const firstName = (data: string[]): string => {
    return getNames({ data, type: 'firstname' });
  };

  const lastName = (data: string[]): string => {
    return getNames({ data, type: 'lastname' });
  };

  // ============================================================================
  // RETORNO DEL HOOK
  // ============================================================================

  return {
    dni,
    placeOfBirth,
    birthDate,
    gender,
    cuil,
    nationality,
    firstName,
    lastName,
    home,
  };
};
